import React from "react";
import "./quotation-print.css";
import eventenaLogo from "../../assets/images/logo-print.png";
import { DEFAULT_PAYMENT_QR_IMAGE_URL, resolveApiAssetUrl } from "../api/client";
import { getEventParticipantKind, getEventParticipantLabels } from "../utils/eventParticipantLabels";
import { numberToIndianCurrencyWords } from "../utils/numberWords";

interface QuotationPrintViewProps {
  quotation: any;
  editing?: boolean;
  draft?: QuotationPrintHeaderDraft;
  onDraftChange?: (key: keyof QuotationPrintHeaderDraft, value: any) => void;
  onPaymentQrUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPaymentQrUseDefault?: () => void;
  paymentQrUploading?: boolean;
  enableColumnVisibilityControls?: boolean;
  quoteNumberReadOnly?: boolean;
}

export interface QuotationPrintHeaderDraft {
  quoteNumber: string;
  issuedByLabel: string;
  status: string;
  title: string;
  clientMobile: string;
  quotationType: string;
  headerTagline: string;
  eventFamily: string;
  eventGroom: string;
  eventBride: string;
  eventDate: string;
  eventVenue: string;
  eventOccasion: string;
  eventDecorType: string;
  paymentQrTitle: string;
  paymentQrNote: string;
  paymentQrImageUrl?: string;
  footerTagline: string;
  footerPhone: string;
  totalNote: string;
  showItemPrice: boolean;
  showItemAmount: boolean;
  showAdjustments: boolean;
  adjustmentsTitle: string;
  showAdjustmentPercentage: boolean;
  showAdjustmentAmount: boolean;
  showPaymentDetails: boolean;
  showScanToPay: boolean;
  paymentMethod: string;
  partialTotalAmount: number;
  partialAmountPaid: number;
  partialTotalManual?: boolean;
  paymentTransactions: Array<{
    paymentMethod: "Cash" | "UPI" | "Bank Transfer" | "Other";
    amountPaid: number;
    paymentDate: string;
    referenceNumber?: string;
  }>;
  sections: Array<{
    id?: number | null;
    templateSectionId?: number | null;
    serviceCategoryId?: number | null;
    sectionCode: string;
    sectionTitle: string;
    sectionDescription?: string | null;
    isVisible: boolean;
    isOptional: boolean;
    showSectionTotal: boolean;
    sectionNote?: string | null;
    items: Array<{
      id?: number | null;
      templateItemId?: number | null;
      catalogItemId?: number | null;
      itemCode: string;
      itemName: string;
      itemDescription?: string | null;
      quantity: number;
      unit?: string | null;
      dimensionText?: string | null;
      pricingMode: "manual" | "quantity_x_rate";
      unitRate: number;
      lineTotal: number;
      discountType: "none" | "flat" | "percent";
      discountValue: number;
      taxRate: number;
      isOptional: boolean;
      includeInTotal: boolean;
      isVisible: boolean;
      remarks?: string | null;
    }>;
  }>;
  notes: Array<{
    templateNoteId?: number | null;
    title: string;
    icon?: string | null;
    isVisible: boolean;
    points: Array<{
      templateNotePointId?: number | null;
      pointText: string;
      isHighlighted: boolean;
    }>;
  }>;
  paymentMilestones: Array<{
    templatePaymentMilestoneId?: number | null;
    milestoneLabel: string;
    percentage: number;
    dueRuleText?: string | null;
    dueDate?: string | null;
    amount: number;
    description?: string | null;
    paymentStatus: string;
    isVisible: boolean;
  }>;
  terms: Array<{
    templateTermId?: number | null;
    termText: string;
    isMandatory: boolean;
    isVisible: boolean;
  }>;
  adjustments: Array<{
    label: string;
    calculationType: "fixed" | "percent";
    calculationValue: number;
    amount: number;
    isVisible: boolean;
  }>;
}

function parseJsonField(value: unknown) {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value) as Record<string, any>;
    } catch (error) {
      return {};
    }
  }

  return value as Record<string, any>;
}

function formatDate(value?: string | null) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

function formatCurrency(value?: number | string | null, currency = "INR") {
  const parsed = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(parsed);
}

function formatPercent(value?: number | string | null) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) {
    return "0%";
  }

  const rounded = Number(parsed.toFixed(2));
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}%`;
}

function formatMobileNumber(value?: string | null) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits || "-";
}

function formatDateInput(value?: string | null) {
  return value ? String(value).slice(0, 10) : "";
}

function getTodayDateInput() {
  return formatDateInput(new Date().toISOString());
}

function normalizePaymentTransactions(value: unknown) {
  const rows = Array.isArray(value) ? value : [];

  return rows.map((row: any) => ({
    paymentMethod: (["Cash", "UPI", "Bank Transfer", "Other"].includes(row.paymentMethod || row.payment_method) ? row.paymentMethod || row.payment_method : "Cash") as "Cash" | "UPI" | "Bank Transfer" | "Other",
    amountPaid: Number(row.amountPaid ?? row.amount_paid ?? row.amount ?? 0),
    paymentDate: formatDateInput(row.paymentDate || row.payment_date) || getTodayDateInput(),
    referenceNumber: String(row.referenceNumber || row.reference_number || "").trim()
  }));
}

function summarizePaymentTransactions(transactions: QuotationPrintHeaderDraft["paymentTransactions"]) {
  return (transactions || []).reduce<Record<string, number>>((summary, transaction) => {
    const method = transaction.paymentMethod || "Cash";
    summary[method] = (summary[method] || 0) + Number(transaction.amountPaid || 0);
    return summary;
  }, {});
}

function isEnabled(value: unknown) {
  return value === undefined || value === null || value === true || value === 1;
}

function calculatePrintItemTotal(item: any) {
  if (item.lineTotal !== undefined && item.lineTotal !== null) {
    return Number(item.lineTotal || 0);
  }

  if (item.line_total !== undefined && item.line_total !== null) {
    return Number(item.line_total || 0);
  }

  const quantity = Number(item.quantity || 0);
  const unitRate = Number(item.unitRate ?? item.unit_rate ?? 0);
  const pricingMode = item.pricingMode || item.pricing_mode || "manual";
  const base = pricingMode === "quantity_x_rate" ? quantity * unitRate : unitRate;
  const discountType = item.discountType || item.discount_type || "none";
  const discountValue = Number(item.discountValue ?? item.discount_value ?? 0);
  const discount =
    discountType === "percent" ? (base * discountValue) / 100 : discountType === "flat" ? discountValue : 0;
  const taxable = Math.max(base - discount, 0);
  return taxable + (taxable * Number(item.taxRate ?? item.tax_rate ?? 0)) / 100;
}

function getPrintItemDescription(item: any) {
  return String(item.itemDescription ?? item.item_description ?? "").trim();
}

function getPrintItemSizeText(item: any) {
  const dimensionText = String(item.dimensionText ?? item.dimension_text ?? "").trim();

  if (dimensionText) {
    return dimensionText;
  }

  const quantity = Number(item.quantity || 0);
  const unit = String(item.unit || "").trim();

  if (quantity && unit) {
    return `${quantity} ${unit}`;
  }

  return quantity ? String(quantity) : "";
}

function calculatePrintSubtotal(sections: any[]) {
  return (sections || []).reduce((sectionSum: number, section: any) => {
    if (!isEnabled(section.isVisible ?? section.is_visible)) {
      return sectionSum;
    }

    return (
      sectionSum +
      (section.items || []).reduce((itemSum: number, item: any) => {
        return itemSum + (isEnabled(item.includeInTotal ?? item.include_in_total) && isEnabled(item.isVisible ?? item.is_visible) ? calculatePrintItemTotal(item) : 0);
      }, 0)
    );
  }, 0);
}

function calculateAdjustmentAmount(adjustment: any, subtotal: number) {
  if (adjustment.amount !== undefined && adjustment.amount !== null) {
    return Number(adjustment.amount || 0);
  }

  const calculationType = adjustment.calculationType || adjustment.calculation_type || "fixed";
  const calculationValue = Number(adjustment.calculationValue ?? adjustment.calculation_value ?? adjustment.amount ?? 0);

  if (calculationType === "percent") {
    return (subtotal * calculationValue) / 100;
  }

  return calculationValue;
}

function calculatePrintGrandTotal(quotation: any) {
  const subtotal = calculatePrintSubtotal(quotation.sections || []);
  const adjustments = (quotation.adjustments || []).reduce((sum: number, adjustment: any) => {
    return sum + (isEnabled(adjustment.isVisible ?? adjustment.is_visible) ? calculateAdjustmentAmount(adjustment, subtotal) : 0);
  }, 0);
  const roundOff = Number(quotation.round_off_amount || 0);
  const discount = Math.max(Number(quotation.quote_discount_total || 0), 0);

  return Math.max(subtotal + adjustments + roundOff - discount, 0);
}

const defaultFooterPhone = "Mobile: +919405076050 +917972426493";
const defaultAdjustmentsTitle = "Event Manager Charges";

function normalizeAdjustmentsTitle(value?: unknown) {
  const title = String(value || "").trim();
  return !title || title === "Additional Charges / Adjustments" ? defaultAdjustmentsTitle : title;
}

function normalizeFooterPhone(value?: unknown) {
  const text = String(value || "").trim();

  if (!text || text.includes("90288") || text.includes("37837")) {
    return defaultFooterPhone;
  }

  return text;
}

function normalizeEventenaLabel(value?: string | null, fallback = "Eventena") {
  const label = String(value || "").trim();
  const normalizedLabel = label.toLowerCase();
  const legacyLabel = ["ash", "vish events"].join("-");

  if (!label || normalizedLabel === legacyLabel) {
    return fallback;
  }

  return label;
}

function InlinePrintEdit({
  editing,
  value,
  field,
  onDraftChange,
  className,
  type = "text",
  multiline = false,
  options
}: {
  editing?: boolean;
  value: string;
  field: keyof QuotationPrintHeaderDraft;
  onDraftChange?: (key: keyof QuotationPrintHeaderDraft, value: any) => void;
  className?: string;
  type?: string;
  multiline?: boolean;
  options?: Array<{ label: string; value: string }>;
}) {
  if (!editing) {
    return <>{value}</>;
  }

  const sharedProps = {
    className: `quote-inline-edit ${className || ""}`.trim(),
    value,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onDraftChange?.(field, event.target.value)
  };

  if (multiline) {
    return <textarea {...sharedProps} rows={2} />;
  }

  if (options?.length) {
    return (
      <select
        className={`quote-inline-edit ${className || ""}`.trim()}
        value={value}
        onChange={(event) => onDraftChange?.(field, event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return <input {...sharedProps} type={type} />;
}

function QuotationTypeEdit({
  editing,
  value,
  onDraftChange
}: {
  editing?: boolean;
  value: string;
  onDraftChange?: (key: keyof QuotationPrintHeaderDraft, value: any) => void;
}) {
  if (!editing) {
    return <>{value || "-"}</>;
  }

  const presetValues = ["Wedding", "Ganpati Bappa Decoration"];
  const presetValue = presetValues.includes(value) ? value : "__custom__";

  return (
    <div className="quote-type-edit-stack">
      <select
        className="quote-inline-edit"
        value={presetValue}
        onChange={(event) => {
          if (event.target.value !== "__custom__") {
            onDraftChange?.("quotationType", event.target.value);
          }
        }}
      >
        <option value="Wedding">Wedding</option>
        <option value="Ganpati Bappa Decoration">Ganpati Bappa Decoration</option>
        <option value="__custom__">Custom</option>
      </select>
      <input
        className="quote-inline-edit"
        placeholder="Enter quotation type"
        value={value}
        onChange={(event) => onDraftChange?.("quotationType", event.target.value)}
      />
    </div>
  );
}

export default function QuotationPrintView({
  quotation,
  editing,
  draft,
  onDraftChange,
  onPaymentQrUpload,
  onPaymentQrUseDefault,
  paymentQrUploading,
  enableColumnVisibilityControls,
  quoteNumberReadOnly
}: QuotationPrintViewProps) {
  if (!quotation) {
    return null;
  }

  const footerConfig = parseJsonField(quotation.footer_config);
  const headerConfig = parseJsonField(quotation.header_config);
  const paymentQrImage = resolveApiAssetUrl(draft?.paymentQrImageUrl ?? footerConfig.payment_qr_image_url) || DEFAULT_PAYMENT_QR_IMAGE_URL;
  const grandTotal = calculatePrintGrandTotal(quotation);
  const grandTotalWords = numberToIndianCurrencyWords(grandTotal);
  const eventOccasion = headerConfig.event_occasion || quotation.event_occasion || "";
  const eventFamily = headerConfig.event_host_family_name || quotation.event_host_family_name || quotation.customer_name || "-";
  const eventGroom = headerConfig.event_groom_name || quotation.event_groom_name || "-";
  const eventBride = headerConfig.event_bride_name || quotation.event_bride_name || "";
  const eventDate = headerConfig.event_start_at || quotation.event_start_at;
  const eventVenue = headerConfig.event_venue || quotation.event_venue || "-";
  const eventDecorType = headerConfig.event_decor_type_label || quotation.event_decor_type_label || "-";
  const clientMobile = headerConfig.client_mobile || quotation.client_mobile || "";
  const quotationType = headerConfig.quotation_type || quotation.quotation_type || "Ganpati Bappa Decoration";
  const eventLabelSource = headerConfig.quotation_event_kind || quotation.quotation_event_kind || eventOccasion || quotation.event_type_name || "";
  const eventParticipantLabels = getEventParticipantLabels(eventLabelSource);
  const shouldShowSecondParticipant = editing || Boolean(String(eventBride || "").trim()) || getEventParticipantKind(eventLabelSource) === "wedding";
  const printValues: QuotationPrintHeaderDraft = draft || {
    quoteNumber: quotation.quote_number || "",
    issuedByLabel: normalizeEventenaLabel(quotation.issued_by_label),
    status: quotation.status || "",
    title: quotation.title || "",
    clientMobile,
    quotationType,
    headerTagline: quotation.header_tagline || "Crafted with care - Executed with excellence",
    eventFamily,
    eventGroom,
    eventBride,
    eventDate: eventDate ? String(eventDate).slice(0, 10) : "",
    eventVenue,
    eventOccasion,
    eventDecorType,
    paymentQrTitle: footerConfig.payment_qr_title || "Scan to Pay",
    paymentQrNote: footerConfig.payment_qr_note || "Use this QR code for booking and milestone payments.",
    paymentQrImageUrl: footerConfig.payment_qr_image_url || "",
    footerTagline: quotation.footer_tagline || "Creating unforgettable moments with professional excellence",
    footerPhone: normalizeFooterPhone(footerConfig.footer_phone || quotation.footer_phone),
    totalNote: quotation.total_note || "Exclusive of diesel, food & extra permissions",
    showItemPrice: quotation.show_item_price !== false && quotation.show_item_price !== 0,
    showItemAmount: quotation.show_item_amount !== false && quotation.show_item_amount !== 0,
    showAdjustments: Boolean((quotation.adjustments || []).length),
    adjustmentsTitle: normalizeAdjustmentsTitle(quotation.adjustments_title),
    showAdjustmentPercentage: true,
    showAdjustmentAmount: true,
    showPaymentDetails: true,
    showScanToPay: footerConfig.show_scan_to_pay !== false && footerConfig.show_scan_to_pay !== 0,
    paymentMethod: quotation.payment_method || "Cash",
    partialTotalAmount: Number(quotation.partial_total_amount || 0),
    partialAmountPaid: Number(quotation.partial_amount_paid || 0),
    partialTotalManual: false,
    paymentTransactions: normalizePaymentTransactions(quotation.payment_transactions),
    sections: quotation.sections || [],
    notes: quotation.notes || [],
    paymentMilestones: quotation.paymentMilestones || [],
    terms: quotation.terms || [],
    adjustments: quotation.adjustments || []
  };
  const displayQuotation = {
    ...quotation,
    show_item_price: printValues.showItemPrice,
    show_item_amount: printValues.showItemAmount,
    sections: printValues.sections?.length ? printValues.sections : quotation.sections,
    adjustments: printValues.showAdjustments ? printValues.adjustments || [] : []
  };
  const visibleSections = (displayQuotation.sections || [])
    .map((section: any, sectionIndex: number) => ({ section, sectionIndex }))
    .filter(({ section }: { section: any }) => isEnabled(section.isVisible ?? section.is_visible));
  const displayGrandTotal = calculatePrintGrandTotal({
    ...displayQuotation,
    sections: visibleSections.map(({ section }: { section: any }) => section)
  });
  const displaySubtotal = calculatePrintSubtotal(visibleSections.map(({ section }: { section: any }) => section));
  const displayGrandTotalWords = numberToIndianCurrencyWords(displayGrandTotal);
  const shouldShowAdjustments = Boolean(printValues.showAdjustments);
  const partialTotalAmount = Number(printValues.partialTotalAmount || 0);
  const paymentTransactions = normalizePaymentTransactions(printValues.paymentTransactions);
  const transactionPaidTotal = paymentTransactions.reduce((sum, transaction) => sum + Number(transaction.amountPaid || 0), 0);
  const partialAmountPaid = paymentTransactions.length ? transactionPaidTotal : Number(printValues.partialAmountPaid || 0);
  const remainingBalance = Math.max(partialTotalAmount - partialAmountPaid, 0);
  const paymentStatusLabel = (printValues.paymentMethod === "Partial Paid" || printValues.paymentMethod === "Paid") && partialTotalAmount > 0 && remainingBalance <= 0 ? "Paid" : printValues.paymentMethod;
  const paymentSummary = summarizePaymentTransactions(paymentTransactions);
  const shouldRenderPaymentDetails = editing || printValues.showPaymentDetails !== false;
  const shouldRenderScanToPay = printValues.showScanToPay !== false;
  const shouldRenderPaymentScheduleSection = editing || Boolean(printValues.paymentMilestones?.length) || printValues.showScanToPay !== false;
  const paymentMethodOptions = ["Cash", "UPI", "Partial Paid", "Paid", "Bank Transfer", "Other"].map((method) => ({ label: method, value: method }));
  const partialTransactionMethodOptions = ["Cash", "UPI", "Bank Transfer", "Other"];
  const statusOptions = [
    "draft",
    "pending",
    "granted",
    "revision_requested",
    "rejected",
    "cancelled",
    "expired",
    "archived"
  ].map((status) => ({ label: status, value: status }));
  const updateNoteTitle = (noteIndex: number, value: string) => {
    const notes = [...printValues.notes];
    notes[noteIndex] = { ...notes[noteIndex], title: value };
    onDraftChange?.("notes", notes);
  };
  const updateNotePoint = (noteIndex: number, pointIndex: number, value: string) => {
    const notes = [...printValues.notes];
    const points = [...notes[noteIndex].points];
    points[pointIndex] = { ...points[pointIndex], pointText: value };
    notes[noteIndex] = { ...notes[noteIndex], points };
    onDraftChange?.("notes", notes);
  };
  const updatePaymentMilestone = (index: number, key: "milestoneLabel" | "percentage" | "description", value: string) => {
    const paymentMilestones = [...printValues.paymentMilestones];
    paymentMilestones[index] = {
      ...paymentMilestones[index],
      [key]: key === "percentage" ? Number(value || 0) : value
    };
    onDraftChange?.("paymentMilestones", paymentMilestones);
  };
  const addPaymentMilestone = () => {
    onDraftChange?.("paymentMilestones", [
      ...(printValues.paymentMilestones || []),
      {
        milestoneLabel: "New Milestone",
        percentage: 0,
        description: "Payment milestone details"
      }
    ]);
  };
  const removePaymentMilestone = (index: number) => {
    onDraftChange?.("paymentMilestones", printValues.paymentMilestones.filter((_: any, currentIndex: number) => currentIndex !== index));
  };
  const addNote = () => {
    onDraftChange?.("notes", [
      ...(printValues.notes || []),
      {
        title: "New Note",
        icon: "",
        isVisible: true,
        points: [{ pointText: "Note details", isHighlighted: false }]
      }
    ]);
  };
  const removeNote = (index: number) => {
    onDraftChange?.("notes", printValues.notes.filter((_: any, currentIndex: number) => currentIndex !== index));
  };
  const addNotePoint = (noteIndex: number) => {
    const notes = [...printValues.notes];
    notes[noteIndex] = {
      ...notes[noteIndex],
      points: [...(notes[noteIndex].points || []), { pointText: "Note point", isHighlighted: false }]
    };
    onDraftChange?.("notes", notes);
  };
  const removeNotePoint = (noteIndex: number, pointIndex: number) => {
    const notes = [...printValues.notes];
    notes[noteIndex] = {
      ...notes[noteIndex],
      points: notes[noteIndex].points.filter((_: any, currentIndex: number) => currentIndex !== pointIndex)
    };
    onDraftChange?.("notes", notes);
  };
  const updateTerm = (index: number, value: string) => {
    const terms = [...printValues.terms];
    terms[index] = { ...terms[index], termText: value };
    onDraftChange?.("terms", terms);
  };
  const addTerm = () => {
    onDraftChange?.("terms", [
      ...(printValues.terms || []),
      { termText: "New term or condition", isMandatory: true, isVisible: true }
    ]);
  };
  const removeTerm = (index: number) => {
    onDraftChange?.("terms", printValues.terms.filter((_: any, currentIndex: number) => currentIndex !== index));
  };
  const updatePrintSection = (sectionIndex: number, changes: Record<string, any>) => {
    const sections = [...printValues.sections];
    sections[sectionIndex] = { ...sections[sectionIndex], ...changes };
    onDraftChange?.("sections", sections);
  };
  const addPrintSection = () => {
    const nextIndex = (printValues.sections || []).length + 1;
    onDraftChange?.("sections", [
      ...(printValues.sections || []),
      {
        sectionCode: String(nextIndex).padStart(2, "0"),
        sectionTitle: "New Section",
        sectionDescription: "",
        isVisible: true,
        isOptional: false,
        showSectionTotal: false,
        sectionNote: "",
        items: [
          {
            itemCode: "01",
            itemName: "New Item",
            itemDescription: "",
            quantity: 1,
            unit: "",
            dimensionText: "",
            pricingMode: "manual",
            unitRate: 0,
            lineTotal: 0,
            discountType: "none",
            discountValue: 0,
            taxRate: 0,
            isOptional: false,
            includeInTotal: true,
            isVisible: true,
            remarks: ""
          }
        ]
      }
    ]);
  };
  const removePrintSection = (sectionIndex: number) => {
    onDraftChange?.("sections", printValues.sections.filter((_: any, currentIndex: number) => currentIndex !== sectionIndex));
  };
  const addPrintItem = (sectionIndex: number) => {
    const sections = [...printValues.sections];
    const section = sections[sectionIndex];
    const nextIndex = (section.items || []).length + 1;
    sections[sectionIndex] = {
      ...section,
      items: [
        ...(section.items || []),
        {
          itemCode: String(nextIndex).padStart(2, "0"),
          itemName: "New Item",
          itemDescription: "",
          quantity: 1,
          unit: "",
          dimensionText: "",
          pricingMode: "manual",
          unitRate: 0,
          lineTotal: 0,
          discountType: "none",
          discountValue: 0,
          taxRate: 0,
          isOptional: false,
          includeInTotal: true,
          isVisible: true,
          remarks: ""
        }
      ]
    };
    onDraftChange?.("sections", sections);
  };
  const removePrintItem = (sectionIndex: number, itemIndex: number) => {
    const sections = [...printValues.sections];
    const section = sections[sectionIndex];
    sections[sectionIndex] = {
      ...section,
      items: section.items.filter((_: any, currentIndex: number) => currentIndex !== itemIndex)
    };
    onDraftChange?.("sections", sections);
  };
  const addAdjustment = () => {
    onDraftChange?.("adjustments", [
      ...(printValues.adjustments || []),
      {
        label: "Additional Charge",
        calculationType: "fixed",
        calculationValue: 0,
        amount: 0,
        isVisible: true
      }
    ]);
  };
  const updateAdjustment = (index: number, changes: Record<string, any>) => {
    const adjustments = [...(printValues.adjustments || [])];
    const current: any = adjustments[index] || {};
    const next = {
      ...current,
      ...changes
    } as any;
    const calculationType = next.calculationType || next.calculation_type || "fixed";
    const calculationValue = Number(next.calculationValue ?? next.calculation_value ?? next.amount ?? 0);
    adjustments[index] = {
      ...next,
      calculationType,
      calculationValue,
      amount: Number(next.amount ?? current.amount ?? 0),
      isVisible: next.isVisible ?? next.is_visible ?? true
    };
    onDraftChange?.("adjustments", adjustments);
  };
  const updateAdjustmentType = (index: number, calculationType: "fixed" | "percent") => {
    updateAdjustment(index, {
      calculationType
    });
  };
  const updateAdjustmentValue = (index: number, value: string) => {
    const calculationValue = Number(value || 0);
    const current: any = (printValues.adjustments || [])[index] || {};
    const calculationType = current.calculationType || current.calculation_type || "fixed";
    updateAdjustment(index, {
      calculationType,
      calculationValue
    });
  };
  const updateAdjustmentAmount = (index: number, value: string) => {
    const amount = Number(value || 0);
    const current: any = (printValues.adjustments || [])[index] || {};
    const calculationType = current.calculationType || current.calculation_type || "fixed";
    updateAdjustment(index, {
      calculationType,
      amount
    });
  };
  const removeAdjustment = (index: number) => {
    onDraftChange?.("adjustments", (printValues.adjustments || []).filter((_: any, currentIndex: number) => currentIndex !== index));
  };
  const updatePrintItem = (sectionIndex: number, itemIndex: number, changes: Record<string, any>) => {
    const sections = [...printValues.sections];
    const section = sections[sectionIndex];
    const items = [...section.items];
    items[itemIndex] = { ...items[itemIndex], ...changes };
    sections[sectionIndex] = { ...section, items };
    onDraftChange?.("sections", sections);
  };
  const updatePrintItemRate = (sectionIndex: number, itemIndex: number, value: string) => {
    const item = printValues.sections[sectionIndex].items[itemIndex];
    const unitRate = Number(value || 0);
    const lineTotal = item.pricingMode === "quantity_x_rate" ? unitRate * Number(item.quantity || 0) : unitRate;
    updatePrintItem(sectionIndex, itemIndex, { unitRate, lineTotal });
  };
  const updatePrintItemAmount = (sectionIndex: number, itemIndex: number, value: string) => {
    const item = printValues.sections[sectionIndex].items[itemIndex];
    const lineTotal = Number(value || 0);
    const quantity = Number(item.quantity || 0);
    const unitRate = item.pricingMode === "quantity_x_rate" && quantity > 0 ? lineTotal / quantity : lineTotal;
    updatePrintItem(sectionIndex, itemIndex, { unitRate, lineTotal });
  };
  const updateMoneyDraftValue = (key: keyof QuotationPrintHeaderDraft, value: string) => {
    onDraftChange?.(key, Math.max(Number(value || 0), 0));
  };
  const updatePaymentTransaction = (index: number, changes: Partial<QuotationPrintHeaderDraft["paymentTransactions"][number]>) => {
    const transactions = [...paymentTransactions];
    transactions[index] = { ...transactions[index], ...changes };
    onDraftChange?.("paymentTransactions", transactions);
  };
  const addPaymentTransaction = () => {
    onDraftChange?.("paymentTransactions", [
      ...paymentTransactions,
      {
        paymentMethod: "Cash",
        amountPaid: Math.max(remainingBalance, 0),
        paymentDate: getTodayDateInput(),
        referenceNumber: ""
      }
    ]);
  };
  const removePaymentTransaction = (index: number) => {
    onDraftChange?.("paymentTransactions", paymentTransactions.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <div className="quote-print-page">
      <div className="quote-page">
        <div className="quote-header">
          <div className="quote-logo-wrap header-logo">
            <img src={eventenaLogo} alt="Eventena" decoding="async" />
          </div>
          <div className="quote-header-title">
            <InlinePrintEdit editing={editing} value={printValues.eventOccasion || "Wedding & Reception"} field="eventOccasion" onDraftChange={onDraftChange} className="title-edit" />
          </div>
          <div className="quote-header-subtitle">
            <InlinePrintEdit editing={editing} value={printValues.title} field="title" onDraftChange={onDraftChange} className="subtitle-edit" multiline />
          </div>
          <div className="quote-gold-line">
            <span />
          </div>
          <div className="quote-header-caption">
            <InlinePrintEdit editing={editing} value={printValues.headerTagline} field="headerTagline" onDraftChange={onDraftChange} className="caption-edit" />
          </div>
        </div>

        <div className="quote-detail-band">
          <div className="quote-detail-item">
            <span className="label">Family Name</span>
            <strong><InlinePrintEdit editing={editing} value={printValues.eventFamily} field="eventFamily" onDraftChange={onDraftChange} /></strong>
          </div>
          <div className="quote-detail-item">
            <span className="label">Name</span>
            <strong><InlinePrintEdit editing={editing} value={printValues.eventGroom} field="eventGroom" onDraftChange={onDraftChange} /></strong>
          </div>
          <div className="quote-detail-item">
            <span className="label">Mobile No</span>
            <strong>
              {editing ? (
                <input
                  className="quote-inline-edit"
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  value={printValues.clientMobile}
                  onChange={(event) => onDraftChange?.("clientMobile", event.target.value.replace(/\D/g, "").slice(0, 10))}
                />
              ) : (
                formatMobileNumber(printValues.clientMobile)
              )}
            </strong>
          </div>
          {shouldShowSecondParticipant ? (
            <div className="quote-detail-item">
              <span className="label">{eventParticipantLabels.secondary}</span>
              <strong><InlinePrintEdit editing={editing} value={printValues.eventBride || "-"} field="eventBride" onDraftChange={onDraftChange} /></strong>
            </div>
          ) : null}
          <div className="quote-detail-item">
            <span className="label">Event Date</span>
            <strong>
              {editing ? (
                <InlinePrintEdit editing={editing} value={printValues.eventDate} field="eventDate" onDraftChange={onDraftChange} type="date" />
              ) : (
                formatDate(printValues.eventDate)
              )}
            </strong>
          </div>
        </div>

        <div className="quote-detail-band secondary">
          <div className="quote-detail-item">
            <span className="label">Quotation Type</span>
            <strong><QuotationTypeEdit editing={editing} value={printValues.quotationType} onDraftChange={onDraftChange} /></strong>
          </div>
          <div className="quote-detail-item">
            <span className="label">Venue</span>
            <strong><InlinePrintEdit editing={editing} value={printValues.eventVenue} field="eventVenue" onDraftChange={onDraftChange} multiline /></strong>
          </div>
          <div className="quote-detail-item">
            <span className="label">Occasion</span>
            <strong><InlinePrintEdit editing={editing} value={printValues.eventOccasion || "-"} field="eventOccasion" onDraftChange={onDraftChange} /></strong>
          </div>
          <div className="quote-detail-item">
            <span className="label">Decor Type</span>
            <strong><InlinePrintEdit editing={editing} value={printValues.eventDecorType} field="eventDecorType" onDraftChange={onDraftChange} /></strong>
          </div>
        </div>

        <div className="quote-meta-row">
          <div>Quotation Ref: <strong>{quoteNumberReadOnly ? (printValues.quoteNumber || "Generated after save") : <InlinePrintEdit editing={editing} value={printValues.quoteNumber} field="quoteNumber" onDraftChange={onDraftChange} />}</strong></div>
          <div>Issued by: <strong><InlinePrintEdit editing={editing} value={printValues.issuedByLabel} field="issuedByLabel" onDraftChange={onDraftChange} /></strong></div>
        </div>

        {editing ? (
          <div className="quote-print-toolbar">
            {enableColumnVisibilityControls ? (
              <>
                <label className="quote-toggle-control">
                  <input type="checkbox" checked={printValues.showItemPrice} onChange={(event) => onDraftChange?.("showItemPrice", event.target.checked)} />
                  <span>Show Rate</span>
                </label>
                <label className="quote-toggle-control">
                  <input type="checkbox" checked={printValues.showItemAmount} onChange={(event) => onDraftChange?.("showItemAmount", event.target.checked)} />
                  <span>Show Amount</span>
                </label>
                <label className="quote-toggle-control">
                  <input type="checkbox" checked={printValues.showAdjustments} onChange={(event) => onDraftChange?.("showAdjustments", event.target.checked)} />
                  <span>Show Additional Charges</span>
                </label>
              </>
            ) : null}
            <button className="quote-print-action" type="button" onClick={addPrintSection}>
              Add section
            </button>
          </div>
        ) : null}

        {visibleSections.map(({ section, sectionIndex }: { section: any; sectionIndex: number }) => (
          <div key={section.id || section.section_code}>
            <div className="quote-section-head">
              <span className="number">
                {editing ? (
                  <input className="quote-inline-edit section-code-edit" value={section.sectionCode || section.section_code || ""} onChange={(event) => updatePrintSection(sectionIndex, { sectionCode: event.target.value })} />
                ) : (
                  section.sectionCode || section.section_code
                )}
              </span>
              <h2>
                {editing ? (
                  <input className="quote-inline-edit section-title-edit" value={section.sectionTitle || section.section_title || ""} onChange={(event) => updatePrintSection(sectionIndex, { sectionTitle: event.target.value })} />
                ) : (
                  section.sectionTitle || section.section_title
                )}
              </h2>
              {editing ? (
                <button className="quote-card-remove inline" type="button" onClick={() => removePrintSection(sectionIndex)}>
                  Remove section
                </button>
              ) : null}
            </div>

            <div className="quote-table-wrap">
              <table className="quote-table">
                <thead>
                  <tr>
                    <th className="quote-col-code">#</th>
                    <th className="quote-col-item">Item / Description</th>
                    <th className="quote-col-qty">Qty / Size</th>
                    {displayQuotation.show_item_price ? <th className="quote-col-money">Rate</th> : null}
                    {displayQuotation.show_item_amount ? <th className="quote-col-money">Amount</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {section.items
                    ?.map((item: any, itemIndex: number) => ({ item, itemIndex }))
                    .filter(({ item }: { item: any }) => isEnabled(item.isVisible ?? item.is_visible))
                    .map(({ item, itemIndex }: { item: any; itemIndex: number }, visibleIndex: number) => (
                    <tr key={item.id || `${section.sectionCode || section.section_code}-${item.itemCode || item.item_code}`}>
                      <td className="quote-col-code"><span className="quote-item-code">{String(visibleIndex + 1).padStart(2, "0")}</span></td>
                      <td className="quote-item-name">
                        {editing ? (
                          <div className="quote-item-edit-stack">
                            <input className="quote-inline-edit item-name-edit" value={item.itemName || item.item_name || ""} onChange={(event) => updatePrintItem(sectionIndex, itemIndex, { itemName: event.target.value })} />
                            <textarea className="quote-inline-edit item-description-edit" rows={2} value={item.itemDescription || item.item_description || ""} onChange={(event) => updatePrintItem(sectionIndex, itemIndex, { itemDescription: event.target.value })} />
                            <button className="quote-card-remove inline" type="button" onClick={() => removePrintItem(sectionIndex, itemIndex)}>
                              Remove item
                            </button>
                          </div>
                        ) : (
                          <>
                            <div>{item.itemName || item.item_name}</div>
                            {getPrintItemDescription(item) ? (
                              <div className="quote-item-description">{getPrintItemDescription(item)}</div>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="quote-col-qty">
                        {editing ? (
                          <input className="quote-inline-edit item-size-edit" value={getPrintItemSizeText(item)} onChange={(event) => updatePrintItem(sectionIndex, itemIndex, { dimensionText: event.target.value })} />
                        ) : (
                          <span className="quote-badge">
                            {getPrintItemSizeText(item) || "-"}
                          </span>
                        )}
                      </td>
                      {displayQuotation.show_item_price ? (
                        <td className="quote-col-money">
                          {editing && printValues.sections?.length ? (
                            <input className="quote-inline-edit quote-money-edit" type="number" value={Number(item.unitRate ?? item.unit_rate ?? 0)} onChange={(event) => updatePrintItemRate(sectionIndex, itemIndex, event.target.value)} />
                          ) : (
                            formatCurrency(item.unitRate ?? item.unit_rate, quotation.currency_code)
                          )}
                        </td>
                      ) : null}
                      {displayQuotation.show_item_amount ? (
                        <td className="quote-col-money">
                          {editing && printValues.sections?.length ? (
                            <input className="quote-inline-edit quote-money-edit" type="number" value={Number(item.lineTotal ?? item.line_total ?? calculatePrintItemTotal(item))} onChange={(event) => updatePrintItemAmount(sectionIndex, itemIndex, event.target.value)} />
                          ) : (
                            formatCurrency(item.lineTotal ?? item.line_total, quotation.currency_code)
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {editing ? (
              <div className="quote-item-actions">
                <button className="quote-print-action" type="button" onClick={() => addPrintItem(sectionIndex)}>
                  Add item
                </button>
              </div>
            ) : null}
          </div>
        ))}

        {shouldShowAdjustments ? (
          <div>
            <div className="quote-section-head">
              <span className="number">ADJ</span>
              <h2>
                {editing ? (
                  <input className="quote-inline-edit section-title-edit" value={normalizeAdjustmentsTitle(printValues.adjustmentsTitle)} onChange={(event) => onDraftChange?.("adjustmentsTitle", event.target.value)} />
                ) : (
                  normalizeAdjustmentsTitle(printValues.adjustmentsTitle)
                )}
              </h2>
              {editing ? (
                <>
                  <label className="quote-toggle-control compact">
                    <input type="checkbox" checked={printValues.showAdjustmentPercentage} onChange={(event) => onDraftChange?.("showAdjustmentPercentage", event.target.checked)} />
                    <span>Show Percentage</span>
                  </label>
                  <label className="quote-toggle-control compact">
                    <input type="checkbox" checked={printValues.showAdjustmentAmount} onChange={(event) => onDraftChange?.("showAdjustmentAmount", event.target.checked)} />
                    <span>Show Amount</span>
                  </label>
                  <button className="quote-print-action" type="button" onClick={addAdjustment}>
                    Add charge
                  </button>
                </>
              ) : null}
            </div>
            <div className="quote-adjustments-panel">
              {printValues.adjustments?.length ? (
                printValues.adjustments.map((adjustment: any, adjustmentIndex: number) => {
                  const calculationType = adjustment.calculationType || adjustment.calculation_type || "fixed";
                  const calculationValue = Number(adjustment.calculationValue ?? adjustment.calculation_value ?? adjustment.amount ?? 0);
                  const amount = calculateAdjustmentAmount(adjustment, displaySubtotal);

                  return (
                    <div className="quote-adjustment-row" key={`adjustment-${adjustmentIndex}`}>
                      {editing ? (
                        <>
                          <input className="quote-inline-edit adjustment-label-edit" value={adjustment.label || ""} onChange={(event) => updateAdjustment(adjustmentIndex, { label: event.target.value })} />
                          <select className="quote-inline-edit adjustment-type-edit" value={calculationType} onChange={(event) => updateAdjustmentType(adjustmentIndex, event.target.value as "fixed" | "percent")}>
                            <option value="fixed">Fixed amount</option>
                            <option value="percent">Percentage</option>
                          </select>
                          {printValues.showAdjustmentPercentage ? (
                            <input className="quote-inline-edit adjustment-value-edit" type="number" title={calculationType === "percent" ? "Percentage" : "Fixed amount"} value={Number(calculationValue.toFixed(2))} onChange={(event) => updateAdjustmentValue(adjustmentIndex, event.target.value)} />
                          ) : null}
                          {printValues.showAdjustmentAmount ? (
                            <input className="quote-inline-edit adjustment-amount-edit" type="number" value={Number(amount.toFixed(2))} onChange={(event) => updateAdjustmentAmount(adjustmentIndex, event.target.value)} />
                          ) : null}
                          <button className="quote-card-remove inline" type="button" onClick={() => removeAdjustment(adjustmentIndex)}>
                            Remove
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="quote-adjustment-label">{adjustment.label || "Additional Charge"}</div>
                          {printValues.showAdjustmentPercentage ? <div className="quote-adjustment-meta">{calculationType === "percent" ? `${calculationValue}%` : "Fixed"}</div> : null}
                          {printValues.showAdjustmentAmount ? <div className="quote-adjustment-amount">{formatCurrency(amount, quotation.currency_code)}</div> : null}
                        </>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="quote-adjustment-empty">
                  {editing ? "No additional charges added." : "No additional charges."}
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div className="quote-total-panel">
          <div>
            <div className="eyebrow">Quotation Total</div>
            <div className="title">Grand Total</div>
            <div className="note">
              {editing ? (
                <input className="quote-inline-edit total-note-edit" value={printValues.totalNote || ""} onChange={(event) => onDraftChange?.("totalNote", event.target.value)} />
              ) : (
                printValues.totalNote || quotation.total_note || "Exclusive of diesel, food & extra permissions"
              )}
            </div>
          </div>
          <div className="value-wrap">
            <div className="value">{formatCurrency(displayGrandTotal, quotation.currency_code)}</div>
            <div className="note">{displayGrandTotalWords}</div>
          </div>
        </div>

        {shouldRenderPaymentDetails ? (
        <div className="quote-payment-method-panel">
          {editing ? (
            <div className="quote-payment-visibility-row">
              <span className="label">Show Payment Details</span>
              <select className="quote-inline-edit" value={printValues.showPaymentDetails === false ? "hide" : "show"} onChange={(event) => onDraftChange?.("showPaymentDetails", event.target.value === "show")}>
                <option value="show">Show</option>
                <option value="hide">Hide</option>
              </select>
            </div>
          ) : null}
          <div className="quote-payment-method-row">
            <span className="label">Payment Status / Method</span>
            <strong><InlinePrintEdit editing={editing} value={printValues.paymentMethod} field="paymentMethod" onDraftChange={onDraftChange} options={paymentMethodOptions} /></strong>
          </div>
          {printValues.paymentMethod === "Partial Paid" || printValues.paymentMethod === "Paid" ? (
            <>
              <div className="quote-partial-payment-grid">
                <div>
                  <span className="label">Total Quotation Amount</span>
                  <strong>
                    {editing ? (
                      <input className="quote-inline-edit quote-money-edit" type="number" min="0" value={partialTotalAmount} onChange={(event) => updateMoneyDraftValue("partialTotalAmount", event.target.value)} />
                    ) : (
                      formatCurrency(partialTotalAmount, quotation.currency_code)
                    )}
                  </strong>
                </div>
                <div>
                  <span className="label">Total Paid</span>
                  <strong>{formatCurrency(partialAmountPaid, quotation.currency_code)}</strong>
                </div>
                <div>
                  <span className="label">Remaining Balance</span>
                  <strong>{formatCurrency(remainingBalance, quotation.currency_code)}</strong>
                </div>
                <div>
                  <span className="label">Payment Status</span>
                  <strong>{paymentStatusLabel}</strong>
                </div>
              </div>
              <div className="quote-payment-summary">
                {Object.entries(paymentSummary).length ? (
                  Object.entries(paymentSummary).map(([method, amount]) => (
                    <div key={method}>
                      <span>{method}</span>
                      <strong>{formatCurrency(amount, quotation.currency_code)}</strong>
                    </div>
                  ))
                ) : (
                  <div>
                    <span>No payments recorded</span>
                    <strong>{formatCurrency(0, quotation.currency_code)}</strong>
                  </div>
                )}
              </div>
              <div className="quote-payment-history">
                <div className="quote-payment-history-head">
                  <span>Payment History</span>
                  {editing ? (
                    <button className="quote-print-action" type="button" onClick={addPaymentTransaction}>
                      Add payment
                    </button>
                  ) : null}
                </div>
                {paymentTransactions.length ? (
                  paymentTransactions.map((transaction, transactionIndex) => (
                    <div className="quote-payment-history-row" key={`partial-payment-${transactionIndex}`}>
                      {editing ? (
                        <>
                          <select className="quote-inline-edit" value={transaction.paymentMethod} onChange={(event) => updatePaymentTransaction(transactionIndex, { paymentMethod: event.target.value as "Cash" | "UPI" | "Bank Transfer" | "Other" })}>
                            {partialTransactionMethodOptions.map((method) => (
                              <option key={method} value={method}>
                                {method}
                              </option>
                            ))}
                          </select>
                          <input className="quote-inline-edit quote-money-edit" type="number" min="0" value={transaction.amountPaid} onChange={(event) => updatePaymentTransaction(transactionIndex, { amountPaid: Math.max(Number(event.target.value || 0), 0) })} />
                          <input className="quote-inline-edit" type="date" max={getTodayDateInput()} value={transaction.paymentDate} onChange={(event) => updatePaymentTransaction(transactionIndex, { paymentDate: event.target.value })} />
                          <input className="quote-inline-edit" value={transaction.referenceNumber || ""} placeholder="Reference no." onChange={(event) => updatePaymentTransaction(transactionIndex, { referenceNumber: event.target.value })} />
                          <button className="quote-card-remove inline" type="button" onClick={() => removePaymentTransaction(transactionIndex)}>
                            Remove
                          </button>
                        </>
                      ) : (
                        <>
                          <span>{transaction.paymentMethod}</span>
                          <strong>{formatCurrency(transaction.amountPaid, quotation.currency_code)}</strong>
                          <span>{formatDate(transaction.paymentDate)}</span>
                          <span>{transaction.referenceNumber || "-"}</span>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="quote-payment-history-empty">No partial payments recorded.</div>
                )}
              </div>
            </>
          ) : null}
        </div>
        ) : null}

        {editing || printValues.notes?.length ? (
          <div>
            <div className="quote-section-head">
              <span className="number">06</span>
              <h2>Important Notes</h2>
              {editing ? (
                <button className="quote-print-action" type="button" onClick={addNote}>
                  Add note
                </button>
              ) : null}
            </div>
            <div className="quote-notes-grid">
              {printValues.notes.map((note: any, noteIndex: number) => (
                <div className="quote-note-card" key={`note-${noteIndex}`}>
                  {editing ? (
                    <button className="quote-card-remove" type="button" onClick={() => removeNote(noteIndex)}>
                      Remove
                    </button>
                  ) : null}
                  <h3>
                    {editing ? (
                      <input className="quote-inline-edit note-title-edit" value={note.title} onChange={(event) => updateNoteTitle(noteIndex, event.target.value)} />
                    ) : (
                      note.title
                    )}
                  </h3>
                  <ul>
                    {note.points?.map((point: any, pointIndex: number) => (
                      <li key={`note-${noteIndex}-point-${pointIndex}`}>
                        {editing ? (
                          <div className="quote-point-edit-row">
                            <textarea className="quote-inline-edit note-point-edit" rows={2} value={point.pointText} onChange={(event) => updateNotePoint(noteIndex, pointIndex, event.target.value)} />
                            <button className="quote-card-remove inline" type="button" onClick={() => removeNotePoint(noteIndex, pointIndex)}>
                              Remove
                            </button>
                          </div>
                        ) : (
                          point.pointText || point.point_text
                        )}
                      </li>
                    ))}
                  </ul>
                  {editing ? (
                    <button className="quote-print-action" type="button" onClick={() => addNotePoint(noteIndex)}>
                      Add point
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {shouldRenderPaymentScheduleSection ? (
          <div>
            <div className="quote-section-head">
              <span className="number">07</span>
              <h2>Payment Schedule</h2>
              {editing ? (
                <button className="quote-print-action" type="button" onClick={addPaymentMilestone}>
                  Add payment
                </button>
              ) : null}
            </div>
            {editing ? (
              <div className="quote-scan-pay-toggle-row print-hidden">
                <span className="label">Show Scan to Pay</span>
                <select className="quote-inline-edit" value={printValues.showScanToPay === false ? "hide" : "show"} onChange={(event) => onDraftChange?.("showScanToPay", event.target.value === "show")}>
                  <option value="show">Show</option>
                  <option value="hide">Hide</option>
                </select>
              </div>
            ) : null}
            <div className="quote-payment-grid">
              {printValues.paymentMilestones.map((milestone: any, milestoneIndex: number) => (
                <div className="quote-payment-card" key={`payment-${milestoneIndex}`}>
                  {editing ? (
                    <button className="quote-card-remove" type="button" onClick={() => removePaymentMilestone(milestoneIndex)}>
                      Remove
                    </button>
                  ) : null}
                  <div className={`percent ${editing ? "percent-editing" : ""}`.trim()}>
                    {editing ? (
                      <input className="quote-inline-edit payment-percent-edit" type="number" value={milestone.percentage} onChange={(event) => updatePaymentMilestone(milestoneIndex, "percentage", event.target.value)} />
                    ) : (
                      formatPercent(milestone.percentage)
                    )}
                  </div>
                  <div className="when">
                    {editing ? (
                      <input className="quote-inline-edit" value={milestone.milestoneLabel} onChange={(event) => updatePaymentMilestone(milestoneIndex, "milestoneLabel", event.target.value)} />
                    ) : (
                      milestone.milestoneLabel || milestone.milestone_label
                    )}
                  </div>
                  <div className="desc">
                    {editing ? (
                      <textarea className="quote-inline-edit" rows={2} value={milestone.description || ""} onChange={(event) => updatePaymentMilestone(milestoneIndex, "description", event.target.value)} />
                    ) : (
                      milestone.description
                    )}
                  </div>
                </div>
              ))}
            </div>
            {shouldRenderScanToPay ? (
              <div className="quote-payment-qr">
                <div>
                  <div className="qr-title">
                    <InlinePrintEdit editing={editing} value={printValues.paymentQrTitle} field="paymentQrTitle" onDraftChange={onDraftChange} />
                  </div>
                  <div className="qr-note">
                    <InlinePrintEdit editing={editing} value={printValues.paymentQrNote} field="paymentQrNote" onDraftChange={onDraftChange} multiline />
                  </div>
                </div>
                <img src={paymentQrImage} alt="Payment QR code" decoding="async" />
                {editing && (onPaymentQrUpload || onPaymentQrUseDefault) ? (
                  <div className="quote-payment-qr-controls print-hidden">
                    {onPaymentQrUpload ? (
                      <label className={`quote-print-action quote-upload-action${paymentQrUploading ? " is-disabled" : ""}`}>
                        {paymentQrUploading ? "Uploading..." : "Upload QR Code"}
                        <input type="file" accept="image/*" disabled={paymentQrUploading} onChange={onPaymentQrUpload} />
                      </label>
                    ) : null}
                    {onPaymentQrUseDefault ? (
                      <button className="quote-print-action" type="button" onClick={onPaymentQrUseDefault} disabled={paymentQrUploading}>
                        Use Default QR
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {editing || printValues.terms?.length ? (
          <div>
            <div className="quote-section-head">
              <span className="number">08</span>
              <h2>Terms & Conditions</h2>
              {editing ? (
                <button className="quote-print-action" type="button" onClick={addTerm}>
                  Add term
                </button>
              ) : null}
            </div>
            <div className="quote-terms-grid">
              {printValues.terms.map((term: any, termIndex: number) => (
                <div className="quote-term-item" key={`term-${termIndex}`}>
                  {editing ? (
                    <div className="quote-term-edit-row">
                      <textarea className="quote-inline-edit term-edit" rows={2} value={term.termText} onChange={(event) => updateTerm(termIndex, event.target.value)} />
                      <button className="quote-card-remove inline" type="button" onClick={() => removeTerm(termIndex)}>
                        Remove
                      </button>
                    </div>
                  ) : (
                    term.termText || term.term_text
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="quote-footer">
          <div>
            <div className="quote-logo-wrap footer-logo">
              <img src={eventenaLogo} alt="Eventena" decoding="async" />
            </div>
          </div>
          <div className="tagline">
            <InlinePrintEdit editing={editing} value={printValues.footerTagline} field="footerTagline" onDraftChange={onDraftChange} multiline />
          </div>
          <div className="contact">
            <InlinePrintEdit editing={editing} value={printValues.footerPhone} field="footerPhone" onDraftChange={onDraftChange} multiline />
          </div>
        </div>
      </div>
    </div>
  );
}
